import type { FC, ReactNode } from 'react';
import { Box, Link, Typography } from '@mui/material';
import { STORE_SUBSCRIPTIONS, type StoreProvider } from './StoreSubscription';

interface StoreManagedSubscriptionProps {
  provider: StoreProvider;
  /** Stated on the text itself: a global rule centers every paragraph in the app. */
  align?: 'left' | 'center';
  /** A further sentence shown after the link, in the same text style as the message. */
  children?: ReactNode;
}

const textStyle = { fontFamily: 'Lato', color: 'inherit' };

/** Tells a store subscriber where the subscription is managed, with a link to that page. */
export const StoreManagedSubscription: FC<StoreManagedSubscriptionProps> = ({
  provider,
  align = 'left',
  children,
}) => {
  const store = STORE_SUBSCRIPTIONS[provider];
  return (
    <Box
      data-testid="store-managed-subscription"
      sx={{ display: 'flex', flexDirection: 'column', gap: 1, textAlign: align }}
    >
      <Typography component="p" sx={{ ...textStyle, textAlign: align }}>
        Your subscription is managed by {store.storeName}. {store.whereToManage}
      </Typography>
      <Link
        href={store.manageUrl}
        target="_blank"
        rel="noopener noreferrer"
        // Only as wide as its words: a column would otherwise stretch the tap area across the row.
        // It keeps its own link colour; the text colour is for the sentences.
        sx={{
          fontFamily: 'Lato',
          fontWeight: 700,
          alignSelf: align === 'center' ? 'center' : 'flex-start',
        }}
      >
        {store.manageLinkLabel}
      </Link>
      {children && (
        <Typography component="p" sx={{ ...textStyle, textAlign: align }}>
          {children}
        </Typography>
      )}
    </Box>
  );
};
